import { externalPatientSchema } from '../components/Patient/forms/patientSchema';

export const EXTERNAL_SYNC_FIELDS = Object.keys(externalPatientSchema?.properties || {});

const DATE_FIELDS = new Set(['dateofbirth', 'assure_dateofbirth']);
const EMAIL_FIELDS = new Set(['email', 'assure_email']);
const PHONE_FIELDS = new Set(['phone', 'assure_phone']);

const normalizeDateValue = (value) => {
  if (!value) return '';

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    return trimmed.slice(0, 10);
  }

  return `${value}`;
};

const normalizeComparableValue = (field, value) => {
  if (value === undefined || value === null) return '';

  if (DATE_FIELDS.has(field)) {
    return normalizeDateValue(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (EMAIL_FIELDS.has(field)) {
      return trimmed.toLowerCase();
    }
    if (PHONE_FIELDS.has(field)) {
      return trimmed.replace(/[^\d+]/g, '');
    }
    return trimmed.toLowerCase();
  }

  if (typeof value === 'boolean') {
    return value ? '1' : '0';
  }

  return `${value}`;
};

const normalizePersistedValue = (field, value) => {
  if (value === undefined || value === null) return value;

  if (DATE_FIELDS.has(field)) {
    return normalizeDateValue(value) || null;
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  return value;
};

const hasIncomingValue = (field, value) => {
  if (value === undefined || value === null) return false;

  if (DATE_FIELDS.has(field)) {
    return !!normalizeDateValue(value);
  }

  if (typeof value === 'string') {
    return !!value.trim();
  }

  return true;
};

const hashString = (value) => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(index);
    hash |= 0;
  }

  return `h${(hash >>> 0).toString(16)}`;
};

export const getExternalValueHash = (field, value) => {
  const normalized = normalizeComparableValue(field, value);
  return hashString(`${field}:${normalized}`);
};

const getIgnoredUpdatesByServiceAndId = (patient, service, externalId) => {
  return patient?.external_sync?.ignored_updates?.[service]?.[`${externalId}`] || {};
};

export const getExternalPatientDifferences = ({ patient, externalPatient, service, externalId, fields = EXTERNAL_SYNC_FIELDS }) => {
  const ignoredHashes = getIgnoredUpdatesByServiceAndId(patient, service, externalId);

  return fields.reduce((differences, field) => {
    const incomingValue = externalPatient?.[field];

    if (!hasIncomingValue(field, incomingValue)) {
      return differences;
    }

    const currentValue = patient?.[field];

    if (normalizeComparableValue(field, incomingValue) === normalizeComparableValue(field, currentValue)) {
      return differences;
    }

    const incomingHash = getExternalValueHash(field, incomingValue);

    differences.push({
      field,
      currentValue,
      incomingValue,
      normalizedIncomingValue: normalizePersistedValue(field, incomingValue),
      incomingHash,
      ignoredByUser: ignoredHashes?.[field] === incomingHash,
    });

    return differences;
  }, []);
};

export const getPendingExternalPatientDifferences = (params) => {
  return getExternalPatientDifferences(params).filter(diff => !diff.ignoredByUser);
};

export const upsertExternalId = (externalIds, service, externalId) => {
  const safeExternalIds = Array.isArray(externalIds) ? externalIds : [];

  return [
    { service, id: `${externalId}` },
    ...safeExternalIds.filter(link => link?.service !== service),
  ];
};

const deleteNestedKeyIfEmpty = (object, keys) => {
  const [firstKey, ...rest] = keys;

  if (!firstKey || !object?.[firstKey]) return;

  if (rest.length === 0) {
    if (Object.keys(object[firstKey]).length === 0) {
      delete object[firstKey];
    }
    return;
  }

  deleteNestedKeyIfEmpty(object[firstKey], rest);

  if (Object.keys(object[firstKey]).length === 0) {
    delete object[firstKey];
  }
};

export const updateExternalSyncMemory = ({ patient, service, externalId, refusedDifferences = [], acceptedFields = [] }) => {
  const nextExternalSync = {
    ...(patient?.external_sync || {}),
    ignored_updates: {
      ...(patient?.external_sync?.ignored_updates || {}),
      [service]: {
        ...(patient?.external_sync?.ignored_updates?.[service] || {}),
        [`${externalId}`]: {
          ...(patient?.external_sync?.ignored_updates?.[service]?.[`${externalId}`] || {}),
        },
      },
    },
  };

  const target = nextExternalSync.ignored_updates[service][`${externalId}`];

  refusedDifferences.forEach(diff => {
    target[diff.field] = diff.incomingHash;
  });

  acceptedFields.forEach(field => {
    delete target[field];
  });

  deleteNestedKeyIfEmpty(nextExternalSync.ignored_updates, [service, `${externalId}`]);

  if (Object.keys(nextExternalSync.ignored_updates || {}).length === 0) {
    delete nextExternalSync.ignored_updates;
  }

  return nextExternalSync;
};

export const buildExternalSyncPutPayload = ({
  patient,
  externalPatient,
  service,
  externalId,
  selectedFields,
  ensureLink = false,
  fields = EXTERNAL_SYNC_FIELDS,
}) => {
  const allDifferences = getExternalPatientDifferences({
    patient,
    externalPatient,
    service,
    externalId,
    fields,
  });

  const selectedSet = new Set(selectedFields || allDifferences.map(diff => diff.field));
  const selectedDifferences = allDifferences.filter(diff => selectedSet.has(diff.field));
  const refusedDifferences = allDifferences.filter(diff => !selectedSet.has(diff.field));
  const values = {};

  selectedDifferences.forEach(diff => {
    values[diff.field] = diff.normalizedIncomingValue;
  });

  if (ensureLink) {
    const nextExternalIds = upsertExternalId(patient?.external_ids, service, externalId);
    const hasExternalIdsChanged = JSON.stringify(nextExternalIds) !== JSON.stringify(patient?.external_ids || []);
    if (hasExternalIdsChanged) {
      values.external_ids = nextExternalIds;
    }
  }

  const nextExternalSync = updateExternalSyncMemory({
    patient,
    service,
    externalId,
    refusedDifferences,
    acceptedFields: selectedDifferences.map(diff => diff.field),
  });

  const hasExternalSyncChanged = JSON.stringify(nextExternalSync || {}) !== JSON.stringify(patient?.external_sync || {});
  if (hasExternalSyncChanged) {
    values.external_sync = nextExternalSync;
  }

  return {
    values,
    allDifferences,
    pendingDifferences: allDifferences.filter(diff => !diff.ignoredByUser),
  };
};

export const shouldRunExternalSyncPut = (values) => {
  return !!values && Object.keys(values).length > 0;
};

export const formatExternalSyncValue = (field, value, t) => {
  if (value === undefined || value === null || value === '') return '—';

  if (typeof value === 'boolean') {
    if (typeof t === 'function') {
      return value ? t('common:yes') : t('common:no');
    }
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if ((field === 'gender' || field === 'assure_gender') && (normalized === 'male' || normalized === 'female') && typeof t === 'function') {
      return t(`form:patient.gender.${normalized}`);
    }
  }

  return `${value}`;
};
